import { useFossabotConnectionStatus } from '../../../../hooks/api/fossabot/useFossabotConnectionStatus';
import { useGitHubAppInstallation } from '../../../../hooks/api/vcs/githubApp/useGitHubAppInstallation';
import type { FossabotStatusResponse } from '../../../../../shared/fossabot/response';

export type FossabotConnectionState =
  | 'connected'
  | 'checking'
  | 'error'
  | 'finishing-setup'
  | 'setup-attention-needed'
  | 'disconnected';

type ConnectionStateOptions = {
  connectionStatus?: FossabotStatusResponse;
  isConnectionLoading: boolean;
  isConnectionError: boolean;
  isGitHubAppInstalled: boolean;
  isInstallationLoading: boolean;
  isInstallationError: boolean;
};

/**
 * Repository connection is the permission to create a PR. Readiness only
 * explains why a disconnected repository is unavailable. A valid repository
 * result takes priority over background query errors.
 */
export function getFossabotConnectionState({
  connectionStatus,
  isConnectionLoading,
  isConnectionError,
  isGitHubAppInstalled,
  isInstallationLoading,
  isInstallationError,
}: ConnectionStateOptions): FossabotConnectionState {
  if (connectionStatus) {
    const { connected: isConnected, statusAvailable: isStatusAvailable, readinessStatus } = connectionStatus;

    if (isConnected) {
      return 'connected';
    }
    if (
      (isGitHubAppInstalled && !isStatusAvailable)
      || readinessStatus === 'LINK_REQUIRED'
      || readinessStatus === 'FAILED'
    ) {
      return 'setup-attention-needed';
    }
    if (isGitHubAppInstalled && (readinessStatus === null || readinessStatus === 'SYNCING')) {
      return 'finishing-setup';
    }
  }

  if (isInstallationLoading || (!connectionStatus && isConnectionLoading)) {
    return 'checking';
  }
  if (isInstallationError || (!connectionStatus && isConnectionError)) {
    return 'error';
  }
  return 'disconnected';
}

/**
 * Combines the organization GitHub App installation and repository connection
 * queries into the connection state displayed by the remediation panel.
 */
export function useFossabotConnection(projectLocator: string) {
  const installation = useGitHubAppInstallation('fossabot');
  const isGitHubAppInstalled = Boolean(installation.data?.installed);
  const connection = useFossabotConnectionStatus(projectLocator, isGitHubAppInstalled);

  const state = getFossabotConnectionState({
    connectionStatus: connection.data,
    isConnectionLoading: connection.isLoading,
    isConnectionError: connection.isError,
    isGitHubAppInstalled,
    isInstallationLoading: installation.isLoading,
    isInstallationError: installation.isError,
  });
  const error = connection.isError ? connection.error : installation.error;

  return { state, error };
}
